import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, ApiError } from "../api/client";
import type { SessionRecord } from "../api/types";
import { EmptyState, ErrorState, LoadingState } from "../components/AsyncState";
import { StatusBadge } from "../components/StatusBadge";
import { formatBytes, formatDate, outcomeLabel } from "../utils/format";

export function SessionListPage() {
  const [sessions, setSessions] = useState<SessionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    try { setSessions(await apiClient.listSessions()); setError(""); }
    catch (reason) { setError(reason instanceof ApiError ? reason.message : "录制列表加载失败"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 15_000);
    return () => window.clearInterval(timer);
  }, [load]);

  const normalized = query.trim().toLowerCase();
  const visible = sessions.filter((session) => !normalized || `${session.name} ${session.session_id}`.toLowerCase().includes(normalized));

  return (
    <>
      <header className="page-header">
        <div><p className="eyebrow">数据录制</p><h1>全部录制</h1><p>来自现场手柄遥操作的原始多模态录制。</p></div>
        <Link className="button primary" to="/organizations">从项目上传</Link>
      </header>
      <section className="toolbar card">
        <label className="search"><span aria-hidden="true">⌕</span><input type="search" placeholder="搜索名称或 Session ID" value={query} onChange={(e) => setQuery(e.target.value)} /></label>
        <span className="record-count">{visible.length} 条记录</span>
      </section>
      {loading ? <LoadingState /> : error ? <ErrorState message={error} retry={() => void load()} /> : visible.length === 0 ? (
        <EmptyState title={sessions.length ? "没有匹配结果" : "还没有录制数据"} detail={sessions.length ? "尝试修改搜索条件。" : "从项目上传第一条手柄遥操作录制，开始云端回放。"} />
      ) : (
        <div className="table-wrap card">
          <table>
            <thead><tr><th>名称</th><th>采集时间</th><th>结果</th><th>时长</th><th>RRD 大小</th><th>状态</th><th><span className="sr-only">操作</span></th></tr></thead>
            <tbody>{visible.map((session) => (
              <tr key={session.session_id}>
                <td><Link className="session-name" to={`/sessions/${encodeURIComponent(session.session_id)}`}>{session.name}</Link><code>{session.session_id}</code></td>
                <td>{formatDate(session.started_at_ns)}</td><td>{outcomeLabel(session.outcome)}</td><td>{session.duration_s.toFixed(1)} 秒</td>
                <td>{formatBytes(session.rrd_size_bytes)}</td><td><StatusBadge status={session.status} /></td>
                <td><Link className="row-link" to={`/sessions/${encodeURIComponent(session.session_id)}`} aria-label={`查看 ${session.name}`}>→</Link></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </>
  );
}
